import { useState, useEffect, useCallback } from 'react';
import './Gallery.css';

const galleryImages = [
  {
    src: '/images/PHOTO-2026-06-15-18-03-41 3.jpg',
    alt: 'Salle de la Brasserie du Petit Bruxelles, poutres blanches et décor chaleureux',
    caption: 'Un cadre d\'exception',
  },
  {
    src: '/images/PHOTO-2026-06-15-18-03-41 2.jpg',
    alt: 'Terrasse extérieure de la brasserie sous une voile d\'ombrage, mobilier coloré',
    caption: 'Notre terrasse ensoleillée',
  },
  {
    src: '/images/PHOTO-2026-06-15-18-03-41 4.jpg',
    alt: 'Foie gras maison servi avec ses toasts et confiture',
    caption: 'Le foie gras maison',
  },
  {
    src: '/images/PHOTO-2026-06-15-18-03-41 5.jpg',
    alt: 'Terrine de volaille maison accompagnée de sa salade fraîche',
    caption: 'Nos entrées du terroir',
  },
  {
    src: '/images/PHOTO-2026-06-15-18-03-41 7.jpg',
    alt: 'Plat mijoté à la bière, spécialité flamande de la brasserie',
    caption: 'Saveurs du terroir',
  },
  {
    src: '/images/PHOTO-2026-06-15-18-03-41.jpg',
    alt: 'Assortiment de mignardises et desserts préparés par notre chef',
    caption: 'Douceurs de notre Chef',
  },
  {
    src: '/images/PHOTO-2026-06-15-18-03-41 6.jpg',
    alt: 'Salle de réception avec cheminée et tables dressées pour les événements',
    caption: 'Réceptions & Événements',
  },
];

function Gallery() {
  const [current, setCurrent] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [direction, setDirection] = useState<'next' | 'prev'>('next');
  const [isAnimating, setIsAnimating] = useState(false);

  const total = galleryImages.length;

  const goTo = useCallback((index: number, dir: 'next' | 'prev') => {
    if (isAnimating) return;
    setIsAnimating(true);
    setDirection(dir);
    setCurrent(index);
    setTimeout(() => setIsAnimating(false), 600);
  }, [isAnimating]);

  const next = useCallback(() => {
    goTo((current + 1) % total, 'next');
  }, [current, total, goTo]);

  const prev = useCallback(() => {
    goTo((current - 1 + total) % total, 'prev');
  }, [current, total, goTo]);

  useEffect(() => {
    if (!isAutoPlaying) return;
    const timer = setInterval(next, 4500);
    return () => clearInterval(timer);
  }, [isAutoPlaying, next]);

  const handleDotClick = (index: number) => {
    setIsAutoPlaying(false);
    goTo(index, index > current ? 'next' : 'prev');
  };

  const prevIndex = (current - 1 + total) % total;
  const nextIndex = (current + 1) % total;

  return (
    <section id="galerie" className="section gallery-section">
      <p className="section__label">Notre Univers</p>
      <h2 className="section__title">Galerie</h2>
      <div className="section__separator" />
      <p className="section__text">
        Plongez dans l'atmosphère chaleureuse de notre brasserie flamande.
      </p>

      <div
        className="gallery"
        onMouseEnter={() => setIsAutoPlaying(false)}
        onMouseLeave={() => setIsAutoPlaying(true)}
      >
        <button className="gallery__arrow gallery__arrow--left" onClick={prev} aria-label="Image précédente">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>

        <div className="gallery__stage">
          <div className="gallery__thumb gallery__thumb--prev" onClick={prev}>
            <img src={galleryImages[prevIndex].src} alt={galleryImages[prevIndex].alt} width={424} height={621} loading="lazy" decoding="async" />
          </div>

          <div className={`gallery__main gallery__main--${direction}`} key={current}>
            <img
              src={galleryImages[current].src}
              alt={galleryImages[current].alt}
              width={424}
              height={621}
              loading="lazy"
              decoding="async"
            />
            <div className="gallery__caption">
              <span className="gallery__caption-text">{galleryImages[current].caption}</span>
              <span className="gallery__counter">{current + 1} / {total}</span>
            </div>
          </div>

          <div className="gallery__thumb gallery__thumb--next" onClick={() => { setIsAutoPlaying(false); next(); }}>
            <img src={galleryImages[nextIndex].src} alt={galleryImages[nextIndex].alt} width={424} height={621} loading="lazy" decoding="async" />
          </div>
        </div>

        <button className="gallery__arrow gallery__arrow--right" onClick={() => { setIsAutoPlaying(false); next(); }} aria-label="Image suivante">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>

        <div className="gallery__dots">
          {galleryImages.map((_, i) => (
            <button
              key={i}
              className={`gallery__dot ${i === current ? 'gallery__dot--active' : ''}`}
              onClick={() => handleDotClick(i)}
              aria-label={`Voir image ${i + 1}`}
            />
          ))}
        </div>

        <div className="gallery__progress">
          <div
            className="gallery__progress-bar"
            style={{
              animationDuration: isAutoPlaying ? '4.5s' : '0s',
              animationPlayState: isAutoPlaying ? 'running' : 'paused',
            }}
            key={isAutoPlaying ? current : 'paused'}
          />
        </div>
      </div>
    </section>
  );
}

export default Gallery;